/* Xiaomi 2026 · Benefits admin · Sprint 2A · Day 2
 *
 * /xiaomi/admin/benefits
 *
 * Features:
 *   • List all 17 benefits grouped by category
 *   • Inline edit cost_cap, name, ordinal, stakeholder, inclusions
 *   • Enable / disable toggle (preserves history)
 *   • Readiness pill: "X of 284 partners ready · Y blocked"
 *   • Add new benefit (modal)
 *   • Audit drawer (same shape as dealer audit)
 *   • CSV export of all 17 benefits
 *
 * Visual: matches .xiaomi-theme.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const CATEGORY_ORDER = ['Gift', 'Experience', 'Concierge', 'Gift (ZSM)'];
const CATEGORY_ICON = {
  Gift: '🎁', Experience: '✨', Concierge: '💬', 'Gift (ZSM)': '👔',
};


export default function XiaomiAdminBenefits() {
  const [rows, setRows] = useState([]);
  const [editableFields, setEditableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeAudit, setActiveAudit] = useState(null);  // benefit_id
  const [showAdd, setShowAdd] = useState(false);
  const [readiness, setReadiness] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/xiaomi/benefits`, auth());
      setRows(r.data.rows);
      setEditableFields(r.data.editable_fields || []);
      // Fetch readiness for each benefit in parallel (small, only 17)
      const readinessByKey = {};
      await Promise.all(r.data.rows.map(async (b) => {
        try {
          const rr = await axios.get(`${API}/xiaomi/benefits/${b._id}`, auth());
          readinessByKey[b._id] = rr.data.readiness;
        } catch (_) { /* tolerate failure */ }
      }));
      setReadiness(readinessByKey);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load benefits');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchBenefit = async (id, field, value) => {
    try {
      const r = await axios.patch(
        `${API}/xiaomi/benefits/${id}`,
        { [field]: value },
        { ...auth(), headers: { ...auth().headers, 'x-edit-source': 'inline' } },
      );
      setRows((rs) => rs.map((b) => b._id === id ? r.data.benefit : b));
      if (r.data.audit?.logged?.length) toast.success(`${field} updated · audited`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
      await load();
    }
  };

  const toggleDisabled = async (b) => {
    setBusy(true);
    try {
      const action = b.disabled ? 'enable' : 'disable';
      await axios.post(`${API}/xiaomi/benefits/${b._id}/${action}`, {}, auth());
      toast.success(`${b.name || b._id} ${action}d`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/benefits/export.csv`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xiaomi_benefits_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Export failed');
    } finally { setBusy(false); }
  };

  if (loading && rows.length === 0) {
    return <div className="text-soft text-sm">Loading benefits…</div>;
  }

  // group by category in canonical order
  const grouped = {};
  for (const cat of CATEGORY_ORDER) grouped[cat] = [];
  for (const b of rows) {
    const cat = b.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(b);
  }

  return (
    <div data-testid="xiaomi-admin-benefits">
      <div className="flex items-end justify-between mb-2">
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif' }}>Benefits</h1>
          <p className="text-soft text-sm">
            {rows.length} benefits · grouped by category · readiness per partner
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost" onClick={exportCsv} disabled={busy}
                  data-testid="xiaomi-benefits-export-csv">
            ⇩ Export CSV
          </button>
          <button className="btn-mi" onClick={() => setShowAdd(true)} disabled={busy}
                  data-testid="xiaomi-benefits-open-add">
            + Add benefit
          </button>
        </div>
      </div>

      {Object.entries(grouped).map(([cat, list]) => list.length > 0 && (
        <section key={cat} className="mb-6">
          <h2 className="text-[12px] uppercase tracking-wider text-soft mb-2 flex items-center gap-2">
            <span style={{ fontSize: 16 }}>{CATEGORY_ICON[cat] || '·'}</span>
            {cat} ({list.length})
          </h2>
          <div className="x-card overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 36 }}>#</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 240 }}>Name</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 120 }}>Stakeholder</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 140 }}>Lifecycle</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 100 }}>Cost cap (₹)</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 180 }}>Readiness</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft">Tier eligibility</th>
                  <th className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft" style={{ width: 90 }}>Status</th>
                  <th className="border-b border-line p-2 text-[10px] uppercase tracking-wider text-soft" style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((b) => {
                  const r = readiness[b._id];
                  return (
                    <tr
                      key={b._id}
                      className="border-b border-line-soft last:border-0 hover:bg-[#faf6ef]"
                      style={{ opacity: b.disabled ? 0.45 : 1 }}
                      data-testid={`xiaomi-benefit-row-${b._id}`}
                    >
                      <td className="p-2 text-soft font-mono">{b.ordinal}</td>
                      <td className="p-2">
                        <InlineCell
                          value={b.name || ''}
                          onSave={(v) => patchBenefit(b._id, 'name', v)}
                          testid={`xiaomi-benefit-name-${b._id}`}
                        />
                        <div className="text-[10.5px] text-soft mt-0.5 font-mono">{b._id}</div>
                      </td>
                      <td className="p-2">
                        <InlineCell
                          value={b.stakeholder || ''}
                          onSave={(v) => patchBenefit(b._id, 'stakeholder', v)}
                          testid={`xiaomi-benefit-stakeholder-${b._id}`}
                        />
                      </td>
                      <td className="p-2 text-soft font-mono text-[11px]">{b.lifecycle_key}</td>
                      <td className="p-2">
                        <InlineCell
                          value={b.cost_cap ?? ''}
                          onSave={(v) => patchBenefit(b._id, 'cost_cap', v === '' ? null : Number(v))}
                          testid={`xiaomi-benefit-cost-${b._id}`}
                        />
                      </td>
                      <td className="p-2">
                        {r ? (
                          <ReadinessBar
                            ready={r.ready_partners}
                            blocked={r.blocked_partners}
                            total={r.total_partners}
                          />
                        ) : <span className="text-soft text-[11px]">…</span>}
                      </td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-1">
                          {(b.tier_eligibility || []).map((t) => (
                            <span key={t} className="chip" style={{ fontSize: 9.5 }}>{t}</span>
                          ))}
                        </div>
                      </td>
                      <td className="p-2">
                        {b.disabled ? (
                          <span className="chip" style={{ background: '#fbe9e3', color: '#c0492f', borderColor: '#fbe9e3' }}>
                            Disabled
                          </span>
                        ) : (
                          <span className="chip" style={{ background: '#dcf8e8', color: '#128c7e', borderColor: '#dcf8e8' }}>
                            Active
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            className="text-[11px] underline text-soft hover:text-ink"
                            onClick={() => setActiveAudit(b._id)}
                            data-testid={`xiaomi-benefit-audit-${b._id}`}
                          >
                            audit
                          </button>
                          <button
                            type="button"
                            className="text-[11px] underline text-soft hover:text-[#c0492f]"
                            onClick={() => toggleDisabled(b)}
                            data-testid={`xiaomi-benefit-toggle-${b._id}`}
                          >
                            {b.disabled ? 'enable' : 'disable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {activeAudit && (
        <BenefitAuditDrawer
          benefitId={activeAudit}
          onClose={() => setActiveAudit(null)}
        />
      )}
      {showAdd && (
        <AddBenefitModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────


function InlineCell({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  useEffect(() => { setDraft(String(value ?? '')); }, [value]);

  if (!editing) {
    return (
      <div
        className="cursor-text px-1 py-0.5 rounded hover:bg-[#fff3e6] truncate"
        onClick={() => setEditing(true)}
        title={String(value || '(click to edit)')}
        style={{ minHeight: 22 }}
        data-testid={testid}
      >
        {value !== '' && value !== null && value !== undefined
          ? String(value)
          : <span className="text-soft italic">—</span>}
      </div>
    );
  }
  return (
    <input
      autoFocus
      className="x-input"
      style={{ fontSize: 12.5, padding: '2px 6px', height: 26 }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (draft !== String(value ?? '')) onSave(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') { setDraft(String(value ?? '')); setEditing(false); }
      }}
      data-testid={`${testid}-input`}
    />
  );
}


function ReadinessBar({ ready, blocked, total }) {
  const pct = total ? Math.round((ready / total) * 100) : 0;
  const color = pct >= 95 ? '#128c7e' : pct >= 60 ? '#d98a1f' : '#c0492f';
  return (
    <div data-testid="xiaomi-benefit-readiness">
      <div className="text-[11px]" style={{ color }}>
        <strong>{ready}</strong> ready
        {blocked > 0 && <span className="text-soft"> · {blocked} blocked</span>}
      </div>
      <div className="w-full h-1.5 rounded mt-1" style={{ background: '#f0ece3' }}>
        <div className="h-full rounded" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}


function BenefitAuditDrawer({ benefitId, onClose }) {
  const [rows, setRows] = useState([]);
  const [benefit, setBenefit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/xiaomi/benefits/${benefitId}/audit?limit=200`, auth()),
          axios.get(`${API}/xiaomi/benefits/${benefitId}`, auth()),
        ]);
        if (!alive) return;
        setRows(a.data.rows); setBenefit(b.data.benefit);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Audit load failed');
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [benefitId]);

  return (
    <Overlay onClose={onClose}>
      <div className="flex items-start justify-between p-6 border-b border-line">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-soft">Audit log</div>
          <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mt-1">
            {benefit?.name || benefitId}
          </h2>
          <div className="text-[12px] text-soft mt-1 font-mono">{benefitId}</div>
        </div>
        <button className="btn-ghost" onClick={onClose}
                data-testid="xiaomi-benefit-audit-close">✕ Close</button>
      </div>
      <div className="overflow-auto flex-1">
        {loading && <div className="p-6 text-soft text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-soft text-sm">
            No edits recorded yet for this benefit.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-[12px]">
            <thead>
              <tr>
                {['When', 'Field', 'Before', 'After', 'By', 'Source'].map((h) => (
                  <th key={h} className="bg-panel2 sticky top-0 p-2 text-left text-[10px] uppercase tracking-wider text-soft border-b border-line">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line-soft last:border-0">
                  <td className="p-2 text-soft" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="p-2 font-mono text-[11px]">{r.field}</td>
                  <td className="p-2"><code className="text-[11px] text-soft">{stringify(r.before)}</code></td>
                  <td className="p-2"><code className="text-[11px]" style={{ color: '#ff6900' }}>{stringify(r.after)}</code></td>
                  <td className="p-2 text-soft text-[11px]">{r.actor_email}</td>
                  <td className="p-2"><span className="chip" style={{ fontSize: 9.5 }}>{r.source}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Overlay>
  );
}


function AddBenefitModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    benefit_id: '', name: '', category: 'Gift', lifecycle_key: 'gift_15_step',
    ordinal: 18, stakeholder: 'Concierge', tier_eligibility: '',
    months_active: '', inclusions: '', cost_cap: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.benefit_id || !form.name) { toast.error('benefit_id + name are required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/xiaomi/benefits`, {
        ...form,
        ordinal: Number(form.ordinal) || 99,
        cost_cap: form.cost_cap ? Number(form.cost_cap) : null,
        tier_eligibility: form.tier_eligibility.split(',').map((s) => s.trim()).filter(Boolean),
        months_active: form.months_active.split(',').map((s) => s.trim()).filter(Boolean),
      }, auth());
      toast.success(`Benefit ${form.benefit_id} created · audited`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} narrow>
      <div className="p-6 border-b border-line">
        <h2 style={{ fontFamily: 'Fraunces, serif' }}>Add a benefit</h2>
        <p className="text-soft text-sm">All 18+ benefits can be edited later. Min: benefit_id + name.</p>
      </div>
      <div className="p-6 grid grid-cols-2 gap-3 overflow-auto flex-1">
        {[
          ['benefit_id', 'Benefit ID (e.g. xiaomi_b18_new_thing)'],
          ['name', 'Display name'],
          ['lifecycle_key', 'Lifecycle key'],
          ['stakeholder', 'Stakeholder owner'],
          ['ordinal', 'Sort order (number)'],
          ['cost_cap', 'Cost cap ₹ (optional)'],
          ['tier_eligibility', 'Tier eligibility (comma-sep)'],
          ['months_active', 'Months active (e.g. Jun,Jul)'],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="text-[11px] text-soft uppercase tracking-wider">{label}</label>
            <input
              className="x-input w-full"
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`xiaomi-benefit-add-${k}`}
            />
          </div>
        ))}
        <div className="col-span-2">
          <label className="text-[11px] text-soft uppercase tracking-wider">Category</label>
          <select
            className="x-input w-full"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="xiaomi-benefit-add-category"
          >
            {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-[11px] text-soft uppercase tracking-wider">Inclusions</label>
          <textarea
            className="x-input w-full"
            style={{ minHeight: 80 }}
            value={form.inclusions}
            onChange={(e) => setForm({ ...form, inclusions: e.target.value })}
            data-testid="xiaomi-benefit-add-inclusions"
          />
        </div>
      </div>
      <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-mi" onClick={submit} disabled={busy} data-testid="xiaomi-benefit-add-submit">
          {busy ? 'Saving…' : 'Add benefit'}
        </button>
      </div>
    </Overlay>
  );
}


function Overlay({ children, onClose, narrow }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{ background: 'rgba(20,15,8,0.45)' }}
      onClick={onClose}
    >
      <div
        className="x-modal flex flex-col w-full h-full"
        style={{ maxWidth: narrow ? 540 : 980, borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}


function stringify(v) {
  if (v === null || v === undefined) return '∅';
  if (v === '') return '""';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
